import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useRealKmClient } from '../../lib/useRealKmClient.js';
import { STATE_LABELS, statoRisorsaLabel } from './kmLabels.js';
import { KM_CONFIG } from '../../api/config.js';
import RisposteSelfAssessmentModal from './RisposteSelfAssessmentModal.jsx';
import EsperienzeConsulenteModal from './EsperienzeConsulenteModal.jsx';
import CandidatoDettaglioModal from './CandidatoDettaglioModal.jsx';

/* ═══════════════════════════════════════════════════════════
   Console HR — Stato Self-Assessment.

   RISCRITTA il 21/09 dopo una scoperta importante: quello che avevo
   costruito prima (basato solo su GET /hr/sa-stato) mostrava SOLO i
   consulenti già staffati — non i candidati in valutazione, che era
   la richiesta originale. Le due popolazioni sono davvero diverse
   (verificato più volte: id, tabelle, endpoint separati).

   GET /hr/sa-da-sollecitare, con solo_da_sollecitare=false per l'intera
   popolazione, è la vista CANDIDATO corretta — verificato dal vivo con
   dati reali (non demo): { risorse: [{ candidato_id, nome, email,
   wa_phone, stato_ciclo_vita, sottostato, ultima_compilazione,
   giorni_dall_ultimo_sa, soglia_giorni, freschezza_sa, sa_da_sollecitare,
   sa_in_corso }], totale, soglia_giorni, solo_da_sollecitare }.
   `sottostato` (R3a/R3b/R3c, null per R1/R2/R4) aggiunto da Armando il
   29/09, dalla stessa v_risorsa_ciclo_vita di /hr/longlist.

   Importante: candidato_id qui è LO STESSO spazio di id di "resourceId"
   usato in ResourceSelfAssessmentPanel/MultifittingDashboard — confermato
   dal collaudo (docstring: "ciclo dei tre mesi, P01" — candidato, non
   consulente). Non più il problema "due mondi" che avevamo con
   consulente.

   Il docstring backend cita esplicitamente UC-SYS-02 dell'Analisi
   Funzionale ("Rilevazione delle risorse del pool con dato non
   aggiornato") — quel caso d'uso dichiara una dipendenza dal
   consolidamento KM per la propria condizione di esclusione (risorse con
   esperienze attive vanno a UC-SYS-03, non qui) — dipendenza che oggi non
   è soddisfatta (consulente.altamira_ref vuoto, verificato). Per questo
   "nome" risulta spesso null nei dati reali: probabile effetto dello
   stesso gap. Mostro "email" come identificativo principale quando nome
   manca, invece di un vuoto.

   GET /hr/sa-stato (consulenti già staffati) resta come scheda SEPARATA,
   sotto — popolazione diversa, utile per motivi diversi (vedere chi ha
   esperienze attive), non va confusa con questa. */

const T = {
  border: 'rgba(0,0,0,0.10)',
  surface: '#FFFFFF',
  surface2: '#F1F0ED',
  txt: '#1A1916',
  txt2: '#6B6A65',
  txt3: '#A3A19C',
  greenBg: '#EAF3DE',
  greenTxt: '#27500A',
  amberBg: '#FAEEDA',
  amberTxt: '#633806',
  redBg: '#FCEBEB',
  redTxt: '#791F1F',
  accentBg: '#E6F1FB',
  accentTxt: '#0C447C',
};

// 29/09 — stessa mappa a 4 stati (§7.2) già usata in MultifittingDashboard,
// tenuta identica per coerenza visiva tra le due schermate.
const STATO_SA_STILE = {
  'Inviato': { bg: T.accentBg, fg: T.accentTxt },
  'In compilazione': { bg: T.amberBg, fg: T.amberTxt },
  'Compilato': { bg: T.greenBg, fg: T.greenTxt },
  'Scaduto': { bg: T.redBg, fg: T.redTxt },
};

// 15/10 — FE-04: nome leggibile del questionario deciso dal sistema (mai il
// codice SA-xx a schermo), per la ripartizione nel riepilogo di invio.
const NOME_PROFILO_SA = {
  SA1: 'Primo contatto (In qualifica)',
  SA2A: 'Proponibile mai staffato',
  SA2B: 'Collaboratore staffato',
  SA2C: 'Collaboratore ex-staffato',
  SA4: 'Chiusura esperienza',
};
const nomeProfiloSa = (p) => NOME_PROFILO_SA[String(p || '').toUpperCase().replace(/[^A-Z0-9]/g, '')] || 'Profilo non indicato';
function ripartizionePerProfilo(lista) {
  const conti = {};
  for (const x of lista || []) {
    const n = nomeProfiloSa(x?.profilo_sa ?? x?.profilo);
    conti[n] = (conti[n] || 0) + 1;
  }
  return Object.entries(conti).map(([n, c]) => `${c} ${n}`).join(' · ');
}

function BadgeStatoSa({ statoUltimoSa, ultimoInvito }) {
  const stile = STATO_SA_STILE[statoUltimoSa] || { bg: T.surface2, fg: T.txt2 };
  const testo = statoUltimoSa === 'Inviato' && ultimoInvito?.created_at
    ? `Inviato il ${new Date(ultimoInvito.created_at).toLocaleDateString('it-IT')}`
    : statoUltimoSa === 'In compilazione' && ultimoInvito?.consumed_at
      ? `In compilazione dal ${new Date(ultimoInvito.consumed_at).toLocaleDateString('it-IT')}`
      : statoUltimoSa || 'Non inviato';
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', background: stile.bg, color: stile.fg }}>
      {testo}
    </span>
  );
}

// 15/10 — SAL 02/10 azione 23: etichette leggibili al posto dei valori tecnici
// (prima «mai compilato», «aggiornato» in minuscolo), coerenti con il badge del
// dettaglio candidato («Profilo aggiornato» / «Profilo da aggiornare»).
const FRESCHEZZA_ETICHETTA = {
  aggiornato: 'Profilo aggiornato',
  scaduto: 'Profilo da aggiornare',
  mai_compilato: 'Mai compilato',
  non_applicabile: 'Non applicabile',
};

function freschezzaColori(f) {
  // Verificato il 26/09 contro il CASE reale in migrations/029: solo
  // quattro valori possibili — non_applicabile, mai_compilato, aggiornato,
  // scaduto. 'da_aggiornare' non esiste mai come valore di freschezza_sa,
  // era solo un controllo difensivo mai verificato — tolto.
  if (f === 'aggiornato') return { background: T.greenBg, color: T.greenTxt };
  if (f === 'mai_compilato' || f === 'scaduto') return { background: T.amberBg, color: T.amberTxt };
  return { background: T.surface2, color: T.txt2 }; // non_applicabile, o valore imprevisto
}

/* ── Scheda 1: CANDIDATI (popolazione corretta, GET /hr/sa-da-sollecitare) ── */

function SchedaCandidati() {
  const kmClient = useRealKmClient();

  const [status, setStatus] = useState('idle');
  const [righe, setRighe] = useState([]);
  const [totale, setTotale] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [techDetails, setTechDetails] = useState(null);
  const [showTech, setShowTech] = useState(false);
  const [search, setSearch] = useState('');
  const [soloDaSollecitare, setSoloDaSollecitare] = useState(false);
  // Aggiunti il 25/09 — Armando ha costruito offset/cerca lato server
  // (POST /hr/sa-da-sollecitare, richiesta di ieri). offset sostituisce
  // il vecchio "limite 500 e basta": ora si può davvero scorrere tutta
  // la popolazione. cercaDebounced evita una chiamata a ogni tasto
  // premuto — 350ms di pausa prima di interrogare il server.
  const [offset, setOffset] = useState(0);
  const [totaleComplessivo, setTotaleComplessivo] = useState(null);
  const [haAltrePagine, setHaAltrePagine] = useState(false);
  const LIMITE_PAGINA = 200;
  const [cercaDebounced, setCercaDebounced] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setCercaDebounced(search); setOffset(0); }, 350);
    return () => clearTimeout(t);
  }, [search]);
  // Aperto il 23/09, su richiesta esplicita emersa in call ("clicco sul
  // nome e vedo le risposte") — { id, nome } | null.
  const [risposteAperte, setRisposteAperte] = useState(null);
  // 30/09 — tasto 👁 per riga: apre il dettaglio completo del candidato
  // (stato SA, invio, storico, transizione di stato) invece della sola
  // vista risposte. candidato_id è lo stesso spazio id di resourceId
  // (vedi nota più sotto su entity_type) — sicuro da passare a
  // CandidatoDettaglioModal.
  const [dettaglioAperto, setDettaglioAperto] = useState(null);
  // Filtro "Solo dati demo" rimosso il 27/09, su richiesta esplicita: la
  // lista mostra sempre l'intera popolazione reale restituita dal backend.

  const [selected, setSelected] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);
  const [riepilogo, setRiepilogo] = useState(null); // UC-SA-01 passo 8
  const [verificando, setVerificando] = useState(false);

  // 20/10 — ordinamento sull'intera popolazione (segnalazione di Marta,
  // confermata da Simone): prima si ordinava solo la pagina caricata (200
  // righe). Ora ordina il backend (d3283a2) prima di limite/offset:
  // ordina = nome | stato | stato_sa | voto_cv, verso = asc | desc; vuoti
  // in coda, stato per ciclo di vita, voto_cv per la scala di Altamira.
  const [sortBy, setSortBy] = useState(null); // 'nome' | 'stato' | 'stato_sa' | 'voto_cv' | null
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  // Ogni cambio di ordinamento riparte dalla prima pagina.
  function handleSort(colonna) {
    if (sortBy === colonna) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(colonna);
      setSortDir('asc');
    }
    setOffset(0);
  }

  const load = React.useCallback(() => {
    setStatus('loading');
    setErrorMessage(null);
    const requestUrl = '/hr/sa-da-sollecitare';
    kmClient
      // limite abbassato a 200 il 25/09: prima era 500 "al massimo che il
      // backend accettava senza offset" — ora che la paginazione vera
      // esiste, una pagina più piccola è più reattiva, senza perdere
      // nulla (si scorre con Avanti/Indietro).
      .get(requestUrl, {
        query: {
          solo_da_sollecitare: soloDaSollecitare,
          limite: LIMITE_PAGINA,
          offset,
          cerca: cercaDebounced || undefined,
          ordina: sortBy || undefined,
          verso: sortBy ? sortDir : undefined,
        },
      })
      .then((data) => {
        const lista = Array.isArray(data?.risorse) ? data.risorse : [];
        setRighe(lista);
        setTotale(data?.totale ?? lista.length);
        setTotaleComplessivo(data?.totale_complessivo ?? null);
        setHaAltrePagine(Boolean(data?.ha_altre_pagine));
        setTechDetails({ requestUrl, status: 200, note: `Risposta ricevuta: ${lista.length} ${lista.length === 1 ? 'riga' : 'righe'}. Soglia: ${data?.soglia_giorni ?? 'n/d'} giorni.` });
        setStatus('ready');
      })
      .catch((err) => {
        setTechDetails({
          requestUrl,
          status: err?.status ?? 'n/d',
          technicalMessage: err?.technicalMessage || null,
          detail: err?.detail || null,
          correlationId: err?.correlationId || null,
        });
        setErrorMessage(err?.detail || 'Errore nel caricamento.');
        setStatus('error');
      });
  }, [kmClient, soloDaSollecitare, offset, cercaDebounced, sortBy, sortDir]);

  useEffect(() => {
    load();
  }, [load]);

  const righeFiltrate = righe;

  // Le righe arrivano già ordinate dal server (vedi handleSort sopra):
  // nessun ordinamento locale, che varrebbe solo sulla pagina corrente.
  const righeOrdinate = righeFiltrate;

  // Stati ammessi come destinatari (reminder_engine.STATI_INVITABILI,
  // verificato 21/09): R2 e R3 soltanto. R1 (non ancora qualificato) e R4
  // (da cancellare) sono esclusi — l'AF lo dice esplicitamente ("R1
  // Inbound: Nessuna somministrazione"). POST /hr/token, a differenza di
  // /hr/invite, non fa QUESTO controllo lato server (verificato leggendo
  // token_gate.issue_token: nessuna validazione di stato) — quindi il
  // blocco deve stare qui, lato nostro, o il link verrebbe generato lo
  // stesso per una persona che non dovrebbe riceverlo.
  const STATI_INVITABILI = new Set(['R2', 'R3']);
  const eInvitabile = (r) => STATI_INVITABILI.has(r.stato_ciclo_vita);

  // 05/10 — colonna "Profilo previsto" rimossa (SAL 02/10, azione 23): il
  // questionario è in corrispondenza 1:1 con lo stato risorsa, già mostrato.

  function toggleSelected(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
    setSendResults(null);
    setRiepilogo(null);
  }

  function toggleSelectAll() {
    const selezionabili = righeFiltrate.filter(eInvitabile);
    setSelected((prev) =>
      selezionabili.every((r) => prev.has(r.candidato_id)) && selezionabili.length > 0
        ? new Set()
        : new Set(selezionabili.map((r) => r.candidato_id))
    );
    setSendResults(null);
  }

  // entity_type: 'risorsa' con candidato_id — QUESTO è confermato corretto
  // (stesso schema già verificato altrove per resourceId di candidato, a
  // differenza del tentativo precedente su consulente_id, mai confermato).
  //
  // Il link generato va catturato SUBITO: il backend lo restituisce una
  // sola volta ("il token non è più recuperabile dal sistema: conservare
  // il link", verificato nella risposta reale di POST /hr/token) — non è
  // richiedibile di nuovo dopo, va mostrato qui o va perso.
  //
  // Blocco V22 aggiunto il 22/09 (rileggendo l'AF per intero): "non è
  // ammessa più di un'istanza aperta per risorsa". POST /hr/token non lo
  // controlla affatto (nessuna validazione, verificato leggendo il
  // sorgente) — senza questo filtro, selezionare qualcuno con sa_in_corso
  // vero genererebbe silenziosamente un secondo link, violando V22 senza
  // alcun avviso. Non offriamo "invia sollecito" in alternativa (non
  // abbiamo un endpoint confermato per quello) — blocchiamo e basta,
  // mostrando chi è stato escluso e perché.
  /* 28/09 — UC-SA-01 dalla notifica di UC-SYS-02 («la notifica è
     azionabile»). Prima l'invio passava da POST /hr/token, che genera un link
     senza alcun controllo né recapito: il profilo non veniva congelato
     all'invio (V19) e nessun messaggio partiva sul canale (V20). Ora usa
     POST /hr/risorse/{id}/invito-sa, con le precondizioni dell'AF applicate
     dal backend, in due passi: verifica (dry_run) con riepilogo per profilo
     di somministrazione, poi conferma esplicita.

     29/09 — canale: 'email' esplicito su ogni chiamata (qui e in
     inviaSa2b più sotto). Decisione di business: per ora solo email, mai
     WhatsApp. Il default lato backend è "auto" (preferisce WhatsApp se la
     risorsa ha un numero, V20) — richiesto ad Armando di cambiarlo, ma
     finché non è fatto lo forziamo qui, per ogni invio. */
  async function handleVerificaSelezione() {
    setVerificando(true);
    setSendResults(null);
    setRiepilogo(null);
    const selezionati = righeFiltrate.filter((x) => selected.has(x.candidato_id) && eInvitabile(x));
    const daInviare = [];
    const daSollecitare = [];
    const esclusi = [];
    // 15/10 — UC-SA-01 FA4: chi ha già un SA aperto non viene escluso ma
    // sollecitato (stesso link, scadenza invariata, nessuna nuova istanza).
    const verificaSollecito = async (r, nome) => {
      try {
        const sol = await kmClient.post(`/hr/risorse/${encodeURIComponent(r.candidato_id)}/sollecito-sa?dry_run=true`);
        if (sol?.esito === 'simulato') daSollecitare.push({ id: r.candidato_id, nome });
        else esclusi.push({ nome, motivo: sol?.motivo || sol?.esito || 'sollecito non possibile' });
      } catch (err) {
        esclusi.push({ nome, motivo: err?.detail || `HTTP ${err?.status ?? 'n/d'}` });
      }
    };
    for (const r of selezionati) {
      const nome = r.nome || r.email || r.candidato_id;
      if (r.sa_in_corso) {
        await verificaSollecito(r, nome);
        continue;
      }
      try {
        const esito = await kmClient.post(`/hr/risorse/${encodeURIComponent(r.candidato_id)}/invito-sa?dry_run=true`, { canale: 'email' });
        if (esito?.esito === 'simulato') daInviare.push({ id: r.candidato_id, nome, profilo: esito.profilo_sa || 'N/D' });
        else if (esito?.sollecito_proponibile) await verificaSollecito(r, nome);
        else esclusi.push({ nome, motivo: esito?.motivo || esito?.esito || 'non invitabile' });
      } catch (err) {
        esclusi.push({ nome, motivo: err?.detail || `HTTP ${err?.status ?? 'n/d'}` });
      }
    }
    setRiepilogo({ daInviare, daSollecitare, esclusi });
    setVerificando(false);
  }

  async function handleConfermaInvio() {
    if (!riepilogo || (riepilogo.daInviare.length + (riepilogo.daSollecitare || []).length) === 0) return;
    setSending(true);
    const ok = [];
    const sollecitati = [];
    const falliti = [...riepilogo.esclusi];
    for (const d of riepilogo.daInviare) {
      try {
        const esito = await kmClient.post(`/hr/risorse/${encodeURIComponent(d.id)}/invito-sa?dry_run=false`, { canale: 'email' });
        if (esito?.esito === 'inviato') ok.push({ nome: d.nome, profilo: esito.profilo_sa || d.profilo });
        else falliti.push({ nome: d.nome, motivo: esito?.motivo || esito?.esito || 'non inviato' });
      } catch (err) {
        falliti.push({ nome: d.nome, motivo: err?.detail || `HTTP ${err?.status ?? 'n/d'}` });
      }
    }
    for (const d of riepilogo.daSollecitare || []) {
      try {
        const esito = await kmClient.post(`/hr/risorse/${encodeURIComponent(d.id)}/sollecito-sa?dry_run=false`);
        if (esito?.esito === 'inviato') sollecitati.push({ nome: d.nome });
        else falliti.push({ nome: d.nome, motivo: esito?.motivo || esito?.esito || 'sollecito non inviato' });
      } catch (err) {
        falliti.push({ nome: d.nome, motivo: err?.detail || `HTTP ${err?.status ?? 'n/d'}` });
      }
    }
    setSendResults({ ok, sollecitati, falliti });
    setRiepilogo(null);
    setSending(false);
    setSelected(new Set());
    load();
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Cerca per nome, email o riferimento Altamira…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '7px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 13, width: '100%', maxWidth: 320 }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={soloDaSollecitare}
            onChange={(e) => { setSoloDaSollecitare(e.target.checked); setOffset(0); setSelected(new Set()); setSendResults(null); }}
          />
          Solo da sollecitare
        </label>
        {status === 'ready' && (
          <span style={{ fontSize: 12, color: T.txt3 }}>
            {totaleComplessivo !== null
              ? `${offset + 1}–${offset + righe.length} di ${totaleComplessivo}`
              : `${totale} in elenco`}
          </span>
        )}
        {status === 'ready' && totaleComplessivo !== null && (offset > 0 || haAltrePagine) && (
          <span style={{ display: 'flex', gap: 6 }}>
            <button
              type="button"
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LIMITE_PAGINA))}
              style={{ padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface, fontSize: 12, cursor: offset === 0 ? 'default' : 'pointer', opacity: offset === 0 ? 0.4 : 1 }}
            >
              ← Precedenti
            </button>
            <button
              type="button"
              disabled={!haAltrePagine}
              onClick={() => setOffset((o) => o + LIMITE_PAGINA)}
              style={{ padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface, fontSize: 12, cursor: !haAltrePagine ? 'default' : 'pointer', opacity: !haAltrePagine ? 0.4 : 1 }}
            >
              Successivi →
            </button>
          </span>
        )}
      </div>

      {status === 'loading' && (
        <div style={{ padding: '2rem', textAlign: 'center', color: T.txt3, background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14 }}>
          Caricamento…
        </div>
      )}

      {status === 'error' && (
        <div style={{ padding: '1rem 1.2rem', borderRadius: 10, background: T.redBg, color: T.redTxt, fontSize: 13 }}>
          {errorMessage}
          <button type="button" onClick={load} style={{ marginLeft: 10, fontSize: 12, cursor: 'pointer' }}>Riprova</button>
        </div>
      )}

      {status === 'ready' && righeFiltrate.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: T.txt3, background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14 }}>
          {search ? 'Nessun risultato per la ricerca corrente.' : 'Nessun candidato in elenco.'}
        </div>
      )}

      {status === 'ready' && righeFiltrate.length > 0 && (
        <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: T.surface2 }}>
                <th style={{ padding: '9px 8px', width: 30 }}>
                  <input type="checkbox" checked={selected.size > 0 && selected.size === righeFiltrate.length} onChange={toggleSelectAll} />
                </th>
                {[
                  { label: 'Nome / Email', sort: 'nome' },
                  { label: 'Stato risorsa', sort: 'stato' },
                  // 01/10 — Voto CV, da data_sources.metadata_json (legato al
                  // solo cvid, non a un annuncio). 20/10: ordinabile, richiesto
                  // da Simone; il server segue la scala di Altamira (Top →
                  // Inaffidabile) e mette in coda chi non ha voto.
                  { label: 'Voto HR', sort: 'voto_cv' },
                  { label: 'Stato ultimo SA', sort: 'stato_sa' },
                  // 05/10 — SAL 02/10 (azione 23): data ultimo SA direttamente in elenco.
                  { label: 'Data ultimo SA', sort: null },
                  { label: '', sort: null },
                ].map((c) => (
                  <th
                    key={c.label || 'azioni'}
                    onClick={c.sort ? () => handleSort(c.sort) : undefined}
                    title={c.sort ? 'Clicca per ordinare' : undefined}
                    style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.txt2, borderBottom: `0.5px solid ${T.border}`, whiteSpace: 'nowrap', cursor: c.sort ? 'pointer' : 'default', userSelect: 'none' }}
                  >
                    {c.label}
                    {c.sort && sortBy === c.sort && (
                      <span style={{ marginLeft: 4, fontSize: 9 }}>{sortDir === 'asc' ? '▲' : '▼'}</span>
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {righeOrdinate.map((r) => {
                const invitabile = eInvitabile(r);
                return (
                <tr key={r.candidato_id} style={{ borderBottom: `0.5px solid ${T.border}`, opacity: invitabile ? 1 : 0.55 }}>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="checkbox"
                      checked={selected.has(r.candidato_id)}
                      onChange={() => toggleSelected(r.candidato_id)}
                      disabled={!invitabile}
                      title={invitabile ? undefined : `Stato «${statoRisorsaLabel(r.stato_ciclo_vita, r.sottostato) || 'sconosciuto'}» non ammesso a ricevere un Self-Assessment (solo In qualifica e Proponibile)`}
                    />
                  </td>
                  <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                    <button
                      type="button"
                      onClick={() => setRisposteAperte({ id: r.candidato_id, nome: r.nome || r.email })}
                      style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 500, color: r.nome ? T.txt : T.txt2, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textDecorationColor: T.border }}
                      title="Vedi le risposte del Self-Assessment"
                    >
                      {r.nome || r.email || '—'}
                    </button>
                    {r.nome && r.email && <div style={{ fontSize: 11, color: T.txt2, fontWeight: 400 }}>{r.email}</div>}
                  </td>
                  <td style={{ padding: '10px 12px' }}>
                    {/* 29/09 — Armando ha aggiunto `sottostato` (R3a/R3b/R3c,
                        null per R1/R2/R4) a /hr/sa-da-sollecitare, dalla
                        stessa v_risorsa_ciclo_vita di /hr/longlist: quando
                        c'è mostra la descrizione estesa (richiesta Simone,
                        punto 12) invece del solo macro-stato "R3". */}
                    {(r.sottostato || r.stato_ciclo_vita)
                      ? (STATE_LABELS[r.sottostato || r.stato_ciclo_vita] || r.sottostato || r.stato_ciclo_vita)
                      : <span style={{ color: T.txt3 }}>N/D</span>}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: T.txt2 }}>
                    {/* 23/10 — SAL 16/10, azione 24: «Voto HR» (campo Voto CV di
                        Altamira), con grafica distinta dal fitting. */}
                    {r.voto_cv
                      ? (
                        <span title="Voto HR (Altamira): giudizio complessivo sulla persona, indipendente dal fitting"
                          style={{ padding: '1px 8px', borderRadius: 6, fontSize: 11.5, border: `0.5px solid ${T.border}`, background: T.surface, color: T.txt, whiteSpace: 'nowrap' }}>
                          {r.voto_cv}
                        </span>
                      )
                      : <span style={{ color: T.txt3 }}>—</span>}
                  </td>
                  {/* 29/09 — Armando ha aggiunto ultimo_invito/stato_ultimo_sa a
                      /hr/sa-da-sollecitare (stessa struttura di longlist e invio
                      singolo): sostituita la vecchia coppia Ultimo SA/Freschezza
                      con lo stato a 4 valori dell'AF (§7.2), coerente con la
                      Dashboard MultiFitting. */}
                  <td style={{ padding: '10px 12px' }}>
                    <BadgeStatoSa statoUltimoSa={r.stato_ultimo_sa} ultimoInvito={r.ultimo_invito} />
                    {r.sa_in_corso && <span style={{ marginLeft: 6, fontSize: 10.5, color: T.txt2 }}>(in corso)</span>}
                  </td>
                  <td style={{ padding: '10px 12px', fontSize: 12, color: T.txt2, whiteSpace: 'nowrap' }}>
                    {r.ultima_compilazione ? new Date(r.ultima_compilazione).toLocaleDateString('it-IT') : <span style={{ color: T.txt3 }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => setDettaglioAperto(r)}
                      title="Apri il dettaglio del candidato"
                      style={{ height: 26, padding: '0 8px', fontSize: 11, border: `0.5px solid ${T.border}`, borderRadius: 6, background: T.surface, cursor: 'pointer' }}
                    >
                      👁
                    </button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>

          <div style={{ padding: 14, borderTop: `0.5px solid ${T.border}`, background: T.surface2 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12, color: T.txt2 }}>{selected.size} selezionati</span>
              <button
                type="button"
                onClick={handleVerificaSelezione}
                disabled={selected.size === 0 || sending || verificando}
                style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: (selected.size === 0 || sending || verificando) ? 'default' : 'pointer', opacity: (selected.size === 0 || sending || verificando) ? 0.5 : 1 }}
              >
                {verificando ? 'Verifica in corso…' : 'Invia Self-Assessment ai selezionati'}
              </button>
            </div>
            {riepilogo && (() => {
              return (
                <div style={{ marginTop: 10, padding: 10, borderRadius: 8, background: T.surface, border: `0.5px solid ${T.border}`, fontSize: 12.5 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>
                    Riepilogo: {riepilogo.daInviare.length} {riepilogo.daInviare.length === 1 ? 'nuovo invio' : 'nuovi invii'}
                    {(riepilogo.daSollecitare || []).length > 0 && ` · ${riepilogo.daSollecitare.length} ${riepilogo.daSollecitare.length === 1 ? 'sollecito' : 'solleciti'}`}
                  </div>
                  {riepilogo.daInviare.length > 0 && (
                    <div style={{ color: T.txt2, marginBottom: 6 }}>
                      Questionari decisi dal sistema: {ripartizionePerProfilo(riepilogo.daInviare)}
                    </div>
                  )}
                  {(riepilogo.daSollecitare || []).length > 0 && (
                    <div style={{ color: T.txt2, marginBottom: 6 }}>
                      Sollecito (Self-Assessment già aperto: nuovo link sulla stessa conversazione, scadenza invariata): {riepilogo.daSollecitare.map((d) => d.nome).join('; ')}
                    </div>
                  )}
                  {riepilogo.esclusi.length > 0 && (
                    <div style={{ color: T.redTxt, marginBottom: 6 }}>
                      Esclusi: {riepilogo.esclusi.map((e) => `${e.nome} (${e.motivo})`).join('; ')}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button type="button" onClick={handleConfermaInvio} disabled={sending || (riepilogo.daInviare.length + (riepilogo.daSollecitare || []).length) === 0}
                      style={{ padding: '6px 14px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', fontSize: 12.5, fontWeight: 600, cursor: sending ? 'default' : 'pointer', opacity: (riepilogo.daInviare.length + (riepilogo.daSollecitare || []).length) === 0 ? 0.5 : 1 }}>
                      {sending ? 'Invio in corso…' : 'Conferma invio'}
                    </button>
                    <button type="button" onClick={() => setRiepilogo(null)} disabled={sending}
                      style={{ padding: '6px 14px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface, fontSize: 12.5, cursor: 'pointer' }}>
                      Annulla
                    </button>
                  </div>
                </div>
              );
            })()}
            <div style={{ fontSize: 10.5, color: T.txt3, marginTop: 6 }}>
              Il questionario è deciso in base allo stato di ciascuna risorsa — non scelto qui.
            </div>
            {sendResults && (
              <div style={{ marginTop: 10, fontSize: 12.5 }}>
                {sendResults.ok.length > 0 && (
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ color: T.greenTxt, fontWeight: 600, marginBottom: 4 }}>✓ {sendResults.ok.length === 1 ? 'Inviato' : 'Inviati'}:</div>
                    {sendResults.ok.map((o, i) => (
                      <div key={i} style={{ marginBottom: 3 }}>
                        {o.nome}
                      </div>
                    ))}
                  </div>
                )}
                {(sendResults.sollecitati || []).length > 0 && (
                  <div style={{ color: T.greenTxt, marginBottom: 6 }}>
                    ✓ {sendResults.sollecitati.length === 1 ? 'Sollecito inviato' : 'Solleciti inviati'}: {sendResults.sollecitati.map((o) => o.nome).join('; ')}
                  </div>
                )}
                {sendResults.falliti.length > 0 && (
                  <div style={{ color: T.redTxt, marginTop: 4 }}>✗ Falliti: {sendResults.falliti.map((f) => `${f.nome} (${f.motivo})`).join('; ')}</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {techDetails && (
        <div style={{ marginTop: 10 }}>
          <button type="button" onClick={() => setShowTech((v) => !v)} style={{ fontSize: 11, color: '#0C447C', background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>
            {showTech ? '▲ Nascondi dettagli tecnici' : '▼ Mostra dettagli tecnici'}
          </button>
          {showTech && (
            <div style={{ marginTop: 6, padding: 8, borderRadius: 6, background: '#fff', border: `0.5px solid ${T.border}`, fontSize: 11, fontFamily: 'monospace', whiteSpace: 'pre-wrap' }}>
              URL: {techDetails.requestUrl}{'\n'}
              Status: {techDetails.status}{'\n'}
              {techDetails.technicalMessage && `Messaggio tecnico: ${techDetails.technicalMessage}\n`}
              {techDetails.detail && `Detail backend: ${techDetails.detail}\n`}
              {techDetails.correlationId && `Correlation id: ${techDetails.correlationId}\n`}
              {techDetails.note && `${techDetails.note}\n`}
            </div>
          )}
        </div>
      )}

      {risposteAperte && (
        <RisposteSelfAssessmentModal
          resourceId={risposteAperte.id}
          nome={risposteAperte.nome}
          tipo="resource"
          onClose={() => setRisposteAperte(null)}
        />
      )}

      {dettaglioAperto && (
        <CandidatoDettaglioModal
          resourceId={dettaglioAperto.candidato_id}
          nome={dettaglioAperto.nome}
          email={dettaglioAperto.email}
          statoCicloVita={dettaglioAperto.stato_ciclo_vita}
          sottostato={dettaglioAperto.sottostato}
          onClose={() => setDettaglioAperto(null)}
          onResourceUpdated={load}
        />
      )}
    </div>
  );
}

/* ── Scheda 2: CONSULENTI GIÀ STAFFATI (GET /hr/sa-stato, popolazione separata) ── */

function SchedaConsulenti() {
  const kmClient = useRealKmClient();
  const [status, setStatus] = useState('idle');
  const [consulenti, setConsulenti] = useState([]);
  const [totale, setTotale] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [esperienzeAperte, setEsperienzeAperte] = useState(null);
  // Aggiunto il 26/09 — /hr/sa-stato non ha un parametro cerca lato
  // server, ma questa popolazione (consulenti già staffati) è piccola e
  // caricata tutta in un colpo, come in D1: filtro lato client, stesso
  // schema già usato lì.
  const [search, setSearch] = useState('');

  const load = React.useCallback(() => {
    setStatus('loading');
    kmClient.get('/hr/sa-stato')
      .then((data) => {
        const lista = Array.isArray(data?.consulenti) ? data.consulenti : [];
        setConsulenti(lista);
        setTotale(data?.totale ?? lista.length);
        setStatus('ready');
      })
      .catch((err) => {
        setErrorMessage(err?.detail || 'Errore nel caricamento.');
        setStatus('error');
      });
  }, [kmClient]);

  useEffect(() => { load(); }, [load]);

  const consulentiCercati = search.trim()
    ? consulenti.filter((c) => {
        const q = search.trim().toLowerCase();
        return (c.nome || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q);
      })
    : consulenti;

  return (
    <div>
      {status === 'loading' && <div style={{ padding: '1.5rem', textAlign: 'center', color: T.txt3 }}>Caricamento…</div>}
      {status === 'error' && <div style={{ padding: '1rem', borderRadius: 10, background: T.redBg, color: T.redTxt, fontSize: 13 }}>{errorMessage}</div>}
      {status === 'ready' && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 10, flexWrap: 'wrap' }}>
            <input
              type="text" placeholder="Cerca per nome o email…" value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ padding: '6px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 13, minWidth: 220 }}
            />
            <span style={{ fontSize: 12, color: T.txt3 }}>
              {search.trim() ? `${consulentiCercati.length} di ${totale}` : `${totale} consulenti`}
            </span>
          </div>
          {consulentiCercati.length === 0 ? (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: T.txt3, background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14 }}>
              {search.trim() ? 'Nessun consulente corrisponde alla ricerca.' : 'Nessun consulente tracciato.'}
            </div>
          ) : (
            <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: T.surface2 }}>
                    {['Nome', 'Contatto', 'Esperienze', 'Data ultimo SA', 'Aggiornamento profilo'].map((h) => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.txt2, borderBottom: `0.5px solid ${T.border}` }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {consulentiCercati.map((c) => (
                    <tr key={c.consulente_id} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                        <button
                          type="button"
                          onClick={() => setEsperienzeAperte({ id: c.consulente_id, nome: c.nome })}
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 500, color: T.txt, cursor: 'pointer', textAlign: 'left', textDecoration: 'underline', textDecorationColor: T.border }}
                          title="Vedi le esperienze e le risposte"
                        >
                          {c.nome || '—'}
                        </button>
                      </td>
                      <td style={{ padding: '10px 12px', color: T.txt2, fontSize: 12 }}>{c.email || '—'}{c.wa_phone && <div>{c.wa_phone}</div>}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <Link
                          to={`/knowledge-management/esperienze?consulente_id=${encodeURIComponent(c.consulente_id)}`}
                          className="btn-link"
                          style={{ fontSize: 12.5 }}
                        >
                          {c.esperienze_aperte ?? '—'} aperte / {c.esperienze_totali ?? '—'} totali
                        </Link>
                      </td>
                      <td style={{ padding: '10px 12px' }}>{c.ultimo_sa ? new Date(c.ultimo_sa).toLocaleDateString('it-IT') : <span style={{ color: T.txt3 }}>—</span>}</td>
                      <td style={{ padding: '10px 12px' }}>
                        {c.freschezza ? (
                          <span className="badge" style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, ...freschezzaColori(c.freschezza) }}>{FRESCHEZZA_ETICHETTA[c.freschezza] || c.freschezza.replace(/_/g, ' ')}</span>
                        ) : <span style={{ color: T.txt3 }}>N/D</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {esperienzeAperte && (
        <EsperienzeConsulenteModal
          consulenteId={esperienzeAperte.id}
          nome={esperienzeAperte.nome}
          onClose={() => setEsperienzeAperte(null)}
        />
      )}
    </div>
  );
}

/* ── Terza scheda: storico invii, da GET /hr/inviti-storico (Monaco,
   22/09). Mostra ogni invio fatto da QUALUNQUE punto del portale — questa
   Console o le tabelle dentro MultiFitting — perché public_token non
   distingue la provenienza. `stato` è calcolato dal backend (aperto /
   scaduto / in_attesa), mai il token in nessuna forma. Nessuna PK
   esposta: la key React usa resource_id+created_at, come indicato da
   Monaco. `entity_type` risulta "risorsa" nei dati reali, non "candidato"
   come nell'esempio iniziale — verificato dal vivo il 22/09, non un
   errore nostro. */
/* 15/10 — SAL 09/10, azione 6: lo Storico invii filtra per stato del
   Self-Assessment (stato_sa, commit 17c438b di Monaco) e sostituisce la
   sotto-scheda «Self-Assessment scaduti» delle Rilevazioni. Una riga per
   invito: sollecito e riemissione del link non creano righe nuove, quindi
   un link revocato non appare come SA scaduto. Filtri e paginazione sono
   lato server (stato_sa, scaduta_dal/scaduta_al, offset, totale_filtrato);
   la ricerca per nome/email resta lato client sulla pagina caricata. */
const STATI_SA_FILTRO = [
  ['', 'Tutti gli stati'],
  ['Inviato', 'Solo inviati (non ancora aperti)'],
  ['In compilazione', 'Solo in compilazione'],
  ['Compilato', 'Solo compilati'],
  ['Scaduto', 'Solo scaduti'],
];
const LIMITE_STORICO = 100;

function SchedaStoricoInvii() {
  const kmClient = useRealKmClient();
  const [status, setStatus] = useState('idle');
  const [inviti, setInviti] = useState([]);
  const [riepilogo, setRiepilogo] = useState(null);
  const [totaleFiltrato, setTotaleFiltrato] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filtroStatoSa, setFiltroStatoSa] = useState('');
  const [scadutaDal, setScadutaDal] = useState('');
  const [scadutaAl, setScadutaAl] = useState('');
  const [offset, setOffset] = useState(0);
  const [ricerca, setRicerca] = useState('');

  const soloScaduti = filtroStatoSa === 'Scaduto';

  const load = React.useCallback(() => {
    setStatus('loading');
    const query = { limite: LIMITE_STORICO, offset };
    if (filtroStatoSa) query.stato_sa = filtroStatoSa;
    if (soloScaduti && scadutaDal) query.scaduta_dal = scadutaDal;
    if (soloScaduti && scadutaAl) query.scaduta_al = scadutaAl;
    kmClient.get('/hr/inviti-storico', { query })
      .then((data) => {
        const lista = Array.isArray(data?.inviti) ? data.inviti : [];
        setInviti(lista);
        setRiepilogo(data?.riepilogo ?? null);
        setTotaleFiltrato(data?.totale_filtrato ?? lista.length);
        setStatus('ready');
      })
      .catch((err) => {
        setErrorMessage(err?.detail || 'Errore nel caricamento dello storico.');
        setStatus('error');
      });
  }, [kmClient, filtroStatoSa, soloScaduti, scadutaDal, scadutaAl, offset]);

  useEffect(() => { load(); }, [load]);

  const cambiaFiltro = (valore) => { setFiltroStatoSa(valore); setOffset(0); };

  const invitiFiltrati = ricerca.trim()
    ? inviti.filter((r) => {
        const q = ricerca.trim().toLowerCase();
        return (r.nome || '').toLowerCase().includes(q) || (r.email || '').toLowerCase().includes(q);
      })
    : inviti;

  const haAltrePagine = totaleFiltrato !== null && offset + inviti.length < totaleFiltrato;
  const fmtGiorno = (ts) => (ts ? new Date(ts).toLocaleDateString('it-IT') : '—');
  const fmtOra = (ts) => (ts ? new Date(ts).toLocaleString('it-IT') : '—');
  const pulsantePagina = (disabilitato) => ({
    padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface,
    fontSize: 12, cursor: disabilitato ? 'default' : 'pointer', opacity: disabilitato ? 0.4 : 1,
  });

  // Riquadri del riepilogo per stato del SA: cliccabili, applicano il filtro.
  const riquadri = riepilogo ? [
    ['', 'Totali', riepilogo.totali],
    ['Inviato', 'Inviati', riepilogo.sa_inviati],
    ['In compilazione', 'In compilazione', riepilogo.sa_in_compilazione],
    ['Compilato', 'Compilati', riepilogo.sa_compilati],
    ['Scaduto', 'Scaduti', riepilogo.sa_scaduti],
  ] : [];

  return (
    <div>
      {riepilogo && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
          {riquadri.map(([valore, label, val]) => {
            const attivo = filtroStatoSa === valore;
            return (
              <button
                key={label}
                type="button"
                onClick={() => cambiaFiltro(valore)}
                title={valore ? `Mostra solo: ${label.toLowerCase()}` : 'Mostra tutti gli invii'}
                style={{
                  padding: '10px 16px', borderRadius: 10, background: T.surface, minWidth: 90, textAlign: 'left', cursor: 'pointer',
                  border: attivo ? '1.5px solid #185FA5' : `0.5px solid ${T.border}`,
                }}
              >
                <div style={{ fontSize: 20, fontWeight: 700 }}>{val ?? '—'}</div>
                <div style={{ fontSize: 11, color: T.txt2 }}>{label}</div>
              </button>
            );
          })}
        </div>
      )}

      <div style={{ marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          placeholder="Cerca per nome o email…"
          value={ricerca}
          onChange={(e) => setRicerca(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 13, minWidth: 220 }}
        />
        <select
          className="field-select"
          style={{ padding: '5px 10px', fontSize: 12.5, width: 'auto' }}
          value={filtroStatoSa}
          onChange={(e) => cambiaFiltro(e.target.value)}
          aria-label="Filtra per stato del Self-Assessment"
        >
          {STATI_SA_FILTRO.map(([v, l]) => <option key={v || 'tutti'} value={v}>{l}</option>)}
        </select>
        {soloScaduti && (
          <span style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12.5, color: T.txt2 }}>
            Scaduti dal
            <input type="date" value={scadutaDal} onChange={(e) => { setScadutaDal(e.target.value); setOffset(0); }}
              style={{ padding: '4px 6px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 12.5 }} />
            al
            <input type="date" value={scadutaAl} onChange={(e) => { setScadutaAl(e.target.value); setOffset(0); }}
              style={{ padding: '4px 6px', borderRadius: 6, border: `0.5px solid ${T.border}`, fontSize: 12.5 }} />
          </span>
        )}
        {status === 'ready' && (
          <span style={{ fontSize: 12, color: T.txt3 }}>
            {ricerca.trim()
              ? `${invitiFiltrati.length} di ${inviti.length} in questa pagina`
              : totaleFiltrato > 0 ? `${offset + 1}–${offset + inviti.length} di ${totaleFiltrato}` : ''}
          </span>
        )}
        {status === 'ready' && (offset > 0 || haAltrePagine) && (
          <span style={{ display: 'flex', gap: 6 }}>
            <button type="button" disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - LIMITE_STORICO))} style={pulsantePagina(offset === 0)}>
              ← Precedenti
            </button>
            <button type="button" disabled={!haAltrePagine}
              onClick={() => setOffset((o) => o + LIMITE_STORICO)} style={pulsantePagina(!haAltrePagine)}>
              Successivi →
            </button>
          </span>
        )}
      </div>

      {soloScaduti && (
        <div style={{ fontSize: 12, color: T.txt3, marginBottom: 10 }}>
          Self-Assessment non compilati entro 30 giorni dall'invio. Le risposte raccolte restano consultabili e la risorsa può essere sollecitata di nuovo.
        </div>
      )}

      {status === 'loading' && <div style={{ padding: '1.5rem', textAlign: 'center', color: T.txt3 }}>Caricamento…</div>}
      {status === 'error' && <div style={{ padding: '1rem', borderRadius: 10, background: T.redBg, color: T.redTxt, fontSize: 13 }}>{errorMessage}</div>}
      {status === 'ready' && (
        invitiFiltrati.length === 0 ? (
          <div style={{ padding: '1.5rem', textAlign: 'center', color: T.txt3, background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14 }}>
            {ricerca.trim() ? 'Nessun invio corrisponde alla ricerca.' : 'Nessun invio trovato.'}
          </div>
        ) : (
          <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 14, overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: T.surface2 }}>
                  {['Destinatario', 'Stato risorsa all\'invio', 'Annuncio', 'Inviato il', 'Stato Self-Assessment', 'Risposte', 'Ultimo sollecito'].map((h) => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: T.txt2, borderBottom: `0.5px solid ${T.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {invitiFiltrati.map((r) => {
                  const stile = STATO_SA_STILE[r.stato_sa];
                  let dettaglioStato = null;
                  if (r.stato_sa === 'Compilato' && r.completata_il) dettaglioStato = `il ${fmtGiorno(r.completata_il)}`;
                  else if (r.stato_sa === 'Scaduto' && r.scaduta_il) dettaglioStato = `il ${fmtGiorno(r.scaduta_il)}`;
                  else if (r.consumed_at) dettaglioStato = `link aperto il ${fmtGiorno(r.consumed_at)}`;
                  else if (r.expires_at) dettaglioStato = `link valido fino al ${fmtGiorno(r.expires_at)}`;
                  return (
                    <tr key={`${r.resource_id}-${r.created_at}`} style={{ borderBottom: `0.5px solid ${T.border}` }}>
                      <td style={{ padding: '10px 12px', fontWeight: 500 }}>
                        {r.nome || <span style={{ fontWeight: 400, color: T.txt2 }}>{r.email || '—'}</span>}
                        {r.nome && r.email && <div style={{ fontSize: 11, color: T.txt2, fontWeight: 400 }}>{r.email}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', color: T.txt2 }}>
                        {r.stato_risorsa_invio
                          ? (STATE_LABELS[r.stato_risorsa_invio] || r.stato_risorsa_invio)
                          : <span style={{ color: T.txt3 }} title="Invio precedente alla registrazione dello stato risorsa">—</span>}
                      </td>
                      <td style={{ padding: '10px 12px', color: T.txt2 }}>{r.jd_titolo || <span style={{ color: T.txt3 }}>fuori da un annuncio</span>}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{fmtOra(r.created_at)}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <span className="badge" style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap', ...(stile ? { background: stile.bg, color: stile.fg } : {}) }}>
                          {r.stato_sa || 'N/D'}
                        </span>
                        {dettaglioStato && <div style={{ fontSize: 11, color: T.txt3, marginTop: 3, whiteSpace: 'nowrap' }}>{dettaglioStato}</div>}
                      </td>
                      <td style={{ padding: '10px 12px', color: r.ha_risposte ? T.txt : T.txt3 }}>
                        {r.ha_risposte ? r.risposte_raccolte : 'nessuna'}
                      </td>
                      <td style={{ padding: '10px 12px', color: T.txt2, whiteSpace: 'nowrap' }}>{r.ultimo_sollecito ? fmtGiorno(r.ultimo_sollecito) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}

/* ── Componente principale: tre schede separate, mai mescolate ── */

/* ── Scheda RILEVAZIONI (28/09) ─────────────────────────────────────────
   Le rilevazioni dei cicli giornalieri dell'AF v2.0 §8.7, che fino a oggi
   finivano solo nel log di audit: nessuna schermata le mostrava a HUB HR.
   - UC-SYS-03 · esperienze in corso con racconto non aggiornato, per
     risorsa (GET /hr/racconto-da-aggiornare). Azionabile: invio del SA-2b
     tramite UC-SA-01, con verifica e conferma.
   - UC-SYS-06 · Self-Assessment scaduti: dal 15/10 (SAL 09/10, azione 6)
     non è più una sotto-scheda qui ma il filtro «Solo scaduti» dello
     Storico invii, che legge lo stato del SA da /hr/inviti-storico.
   UC-SYS-02 è la scheda «Candidati» con «Solo da sollecitare»; UC-SYS-04 è
   la pagina «Esperienze in scadenza».

   TOLTA 29/09: la sezione "SA-4 in attesa/senza recapito" (UC-SYS-05).
   L'AF descrive UC-SYS-05 come un processo automatico e silenzioso (V3:
   "l'unico invio generato dal sistema senza intervento manuale di HR"),
   senza una schermata di consultazione — era un'aggiunta mia per rendere
   visibile lo stato di blocco (V22), non richiesta dall'AF. ─────────── */
function fmtData(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? String(ts) : d.toLocaleDateString('it-IT');
}

function Sezione({ titolo, sottotitolo, stato, errore, vuoto, children }) {
  return (
    <div style={{ background: T.surface, border: `0.5px solid ${T.border}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700 }}>{titolo}</div>
      {sottotitolo && <div style={{ fontSize: 11.5, color: T.txt3, marginTop: 2, marginBottom: 10 }}>{sottotitolo}</div>}
      {stato === 'loading' && <div style={{ fontSize: 12.5, color: T.txt3 }}>Caricamento…</div>}
      {stato === 'error' && <div style={{ fontSize: 12.5, color: T.redTxt }}>⚠ {errore}</div>}
      {stato === 'ready' && vuoto && <div style={{ fontSize: 12.5, color: T.txt3 }}>Nessuna segnalazione.</div>}
      {stato === 'ready' && !vuoto && children}
    </div>
  );
}

function useRilevazione(kmClient, url, query) {
  const [stato, setStato] = useState('loading');
  const [dati, setDati] = useState(null);
  const [errore, setErrore] = useState(null);
  const [tick, setTick] = useState(0);
  const chiave = JSON.stringify(query || {});
  useEffect(() => {
    let annullato = false;
    setStato('loading');
    kmClient.get(url, query ? { query } : undefined)
      .then((d) => { if (!annullato) { setDati(d); setStato('ready'); } })
      .catch((err) => { if (!annullato) { setErrore(err?.detail || 'Rilevazione non disponibile.'); setStato('error'); } });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kmClient, url, chiave, tick]);
  return { stato, dati, errore, ricarica: () => setTick((t) => t + 1) };
}

function SchedaRilevazioni() {
  const kmClient = useRealKmClient();
  const racconto = useRilevazione(kmClient, '/hr/racconto-da-aggiornare');
  // 02/10 — richieste di colloquio emerse dal Self-Assessment conversazionale
  // (Funzionalità 9): la chat le registra in audit_log (sa_richiesta_colloquio_hr,
  // solo dopo conferma esplicita della persona) e GET /hr/richieste-colloquio
  // le legge — endpoint già pronto lato backend (Armando), ma senza alcuna
  // schermata che lo chiamasse: la chat diceva "ho segnalato a HR" e HR non
  // aveva modo di scoprirlo. Nessuna modifica backend: solo questa lettura.
  const colloqui = useRilevazione(kmClient, '/hr/richieste-colloquio');
  const [dettaglioAperto, setDettaglioAperto] = useState(null);
  const [invio, setInvio] = useState({}); // candidato_id -> { fase, messaggio }

  // 02/10 — tre sotto-schede con selettore orizzontale invece di tre blocchi
  // impilati: con liste lunghe si perdeva la seconda/terza sezione fuori
  // schermo. Stesso pattern del selettore principale di questa pagina.
  const [subTab, setSubTab] = useState('colloqui');

  // 02/10 — il nome manca spesso su questa chiamata (stesso gap noto di
  // consulente.altamira_ref vuoto, già documentato sopra per la scheda
  // Candidati), e qui /hr/richieste-colloquio non porta nemmeno l'email come
  // ripiego. Nessuna modifica backend: si recupera nome/email con una
  // chiamata in più a GET /resources/{id} (già usata dal pannello di
  // dettaglio), solo per le righe che ne hanno bisogno.
  const [dettagliRisorsa, setDettagliRisorsa] = useState({});
  const richiesteColloquioGrezze = colloqui.dati?.richieste || [];
  useEffect(() => {
    const daRecuperare = [...new Set(
      richiesteColloquioGrezze
        .filter((r) => !r.nome && r.resource_id && !dettagliRisorsa[r.resource_id])
        .map((r) => r.resource_id)
    )];
    if (daRecuperare.length === 0) return;
    let annullato = false;
    Promise.all(daRecuperare.map((id) =>
      kmClient.get(`/resources/${encodeURIComponent(id)}`)
        .then((d) => [id, { nome: d?.anagrafica?.nome || null, email: d?.anagrafica?.email || null }])
        .catch(() => [id, {}])
    )).then((coppie) => {
      if (annullato) return;
      setDettagliRisorsa((prev) => {
        const next = { ...prev };
        for (const [id, info] of coppie) next[id] = info;
        return next;
      });
    });
    return () => { annullato = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [richiesteColloquioGrezze]);

  function etichettaRisorsa(r) {
    const extra = dettagliRisorsa[r.resource_id] || {};
    return r.nome || extra.nome || extra.email || r.resource_id;
  }

  async function inviaSa2b(id, conferma) {
    setInvio((p) => ({ ...p, [id]: { fase: conferma ? 'sending' : 'checking' } }));
    try {
      const r = await kmClient.post(`/hr/risorse/${encodeURIComponent(id)}/invito-sa?dry_run=${conferma ? 'false' : 'true'}`, { canale: 'email' });
      if (!conferma && r?.esito === 'simulato') {
        setInvio((p) => ({ ...p, [id]: { fase: 'confirm', messaggio: `Verrà inviato il Self-Assessment di aggiornamento. Confermi?` } }));
      } else if (conferma && r?.esito === 'inviato') {
        setInvio((p) => ({ ...p, [id]: { fase: 'sent', messaggio: `✓ Inviato` } }));
        racconto.ricarica();
      } else {
        setInvio((p) => ({ ...p, [id]: { fase: 'error', messaggio: `Invio non possibile: ${r?.motivo || r?.esito || 'non invitabile'}` } }));
      }
    } catch (err) {
      setInvio((p) => ({ ...p, [id]: { fase: 'error', messaggio: err?.detail || 'Errore durante l\'invio.' } }));
    }
  }

  const perRisorsa = racconto.dati?.per_risorsa || [];
  const richiesteColloquio = richiesteColloquioGrezze;
  const cella = { padding: '6px 8px', fontSize: 12.5, borderBottom: `0.5px solid ${T.border}`, verticalAlign: 'top' };

  const SOTTOSCHEDE = [
    ['colloqui', 'Richieste di colloquio con HR'],
    ['racconto', 'Racconto delle esperienze da aggiornare'],
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: `1px solid ${T.border}` }}>
        {SOTTOSCHEDE.map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setSubTab(key)}
            style={{
              padding: '7px 14px',
              border: 'none',
              background: 'none',
              fontSize: 12.5,
              fontWeight: 600,
              color: subTab === key ? '#185FA5' : T.txt2,
              borderBottom: subTab === key ? '2px solid #185FA5' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {subTab === 'colloqui' && (
        <Sezione
          titolo="Richieste di colloquio con HR"
          sottotitolo="Emerse nel Self-Assessment conversazionale quando la persona conferma esplicitamente di volersi confrontare con HR su crescita, mobilità o insoddisfazione (human-in-the-loop: nessuna segnalazione senza conferma)."
          stato={colloqui.stato} errore={colloqui.errore} vuoto={richiesteColloquio.length === 0}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Risorsa', 'Temi emersi', 'Richiesto il', ''].map((h) => <th key={h} style={{ ...cella, fontSize: 11, color: T.txt2, textAlign: 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {richiesteColloquio.map((r) => (
                <tr key={`${r.resource_id}-${r.richiesta_il}`}>
                  <td style={cella}>{etichettaRisorsa(r)}</td>
                  <td style={cella}>
                    {Array.isArray(r.temi) && r.temi.length > 0
                      ? r.temi.join(', ')
                      : <span style={{ color: T.txt3 }}>non specificati</span>}
                  </td>
                  <td style={cella}>{fmtData(r.richiesta_il)}</td>
                  <td style={{ ...cella, textAlign: 'right' }}>
                    <button
                      type="button"
                      onClick={() => setDettaglioAperto(r)}
                      title="Apri il dettaglio del candidato"
                      style={{ height: 26, padding: '0 8px', fontSize: 11, border: `0.5px solid ${T.border}`, borderRadius: 6, background: T.surface, cursor: 'pointer' }}
                    >
                      👁
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Sezione>
      )}

      {subTab === 'racconto' && (
        <Sezione
          titolo="Racconto delle esperienze da aggiornare"
          sottotitolo="Risorse staffate il cui timer trimestrale è maturato. Un solo Self-Assessment per risorsa, con una sezione per ciascuna esperienza in corso (V17)."
          stato={racconto.stato} errore={racconto.errore} vuoto={perRisorsa.length === 0}
        >
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>{['Risorsa', 'Esperienze in corso', 'Ultimo aggiornamento del dato', ''].map((h) => <th key={h} style={{ ...cella, fontSize: 11, color: T.txt2, textAlign: 'left' }}>{h}</th>)}</tr></thead>
            <tbody>
              {perRisorsa.map((r) => {
                const st = invio[r.candidato_id] || {};
                return (
                  <tr key={r.candidato_id}>
                    <td style={cella}>{r.nome || r.candidato_id}</td>
                    <td style={cella}>
                      {(r.esperienze || []).map((e) => (
                        <div key={e.esperienza_id}>
                          <Link to={`/knowledge-management/esperienze/${encodeURIComponent(e.esperienza_id)}`} style={{ color: '#0C447C' }}>
                            {e.denominazione_progetto || 'Progetto'}{e.cliente_finale ? ` · ${e.cliente_finale}` : ''}
                          </Link>
                          <span style={{ color: T.txt3 }}> — ultimo racconto {fmtData(e.ultimo_racconto_esperienza)}</span>
                        </div>
                      ))}
                    </td>
                    <td style={cella}>{fmtData(r.ultimo_aggiornamento_dato)}</td>
                    <td style={{ ...cella, whiteSpace: 'nowrap' }}>
                      {st.fase === 'confirm' ? (
                        <>
                          <div style={{ fontSize: 11.5, marginBottom: 4 }}>{st.messaggio}</div>
                          <button type="button" onClick={() => inviaSa2b(r.candidato_id, true)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#185FA5', color: '#fff', fontSize: 12, cursor: 'pointer' }}>Conferma</button>
                          <button type="button" onClick={() => setInvio((p) => ({ ...p, [r.candidato_id]: {} }))} style={{ marginLeft: 6, padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface, fontSize: 12, cursor: 'pointer' }}>Annulla</button>
                        </>
                      ) : st.fase === 'sent' || st.fase === 'error' ? (
                        <span style={{ fontSize: 12, color: st.fase === 'sent' ? T.greenTxt : T.redTxt }}>{st.messaggio}</span>
                      ) : (
                        <button type="button" disabled={st.fase === 'checking' || st.fase === 'sending'} onClick={() => inviaSa2b(r.candidato_id, false)}
                          style={{ padding: '4px 10px', borderRadius: 6, border: `0.5px solid ${T.border}`, background: T.surface, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                          {st.fase === 'checking' ? 'Verifica…' : 'Invia Self-Assessment'}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Sezione>
      )}

      <div style={{ fontSize: 12.5, marginTop: 4 }}>
        <Link to="/knowledge-management/esperienze-in-scadenza" style={{ color: '#0C447C' }}>Esperienze in scadenza →</Link>
      </div>

      {dettaglioAperto && (
        <CandidatoDettaglioModal
          resourceId={dettaglioAperto.resource_id}
          nome={dettaglioAperto.nome}
          statoCicloVita={dettaglioAperto.stato_ciclo_vita}
          onClose={() => setDettaglioAperto(null)}
        />
      )}
    </div>
  );
}

export default function HrSelfAssessmentStatusPage() {
  // Aggiunto il 26/09 — legge la scheda iniziale da ?tab= nell'URL, così
  // un link esterno (es. da D1 dentro MultiFitting) può portare
  // direttamente su "Storico invii" invece di aprire sempre su
  // "Candidati" di default. Valore non riconosciuto → ricade su
  // 'candidati', comportamento identico a prima.
  const [searchParams] = useSearchParams();
  const tabIniziale = ['candidati', 'consulenti', 'rilevazioni', 'storico'].includes(searchParams.get('tab'))
    ? searchParams.get('tab')
    : 'candidati';
  const [tab, setTab] = useState(tabIniziale);

  // 12/10 — contenitore più largo (come la lista Esperienze): le tabelle della
  // console hanno molte colonne e a 1000px le intestazioni andavano a capo.
  // Cambia solo lo spazio disponibile, non le colonne.
  return (
    <div style={{ maxWidth: 1480, margin: '0 auto', padding: '24px 16px' }}>
      <Link to="/" style={{ fontSize: 12.5, color: T.txt2, textDecoration: 'none', display: 'inline-block', marginBottom: 10 }}>
        ← Hub
      </Link>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>Console HR — Stato Self-Assessment</h1>
        {KM_CONFIG.HR_CONSOLE_MOCK_MODE && (
          <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 10.5, fontWeight: 700, background: T.amberBg, color: T.amberTxt }}>
            DATI DI SVILUPPO — non demo
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 4, marginTop: 14, marginBottom: 20, borderBottom: `1px solid ${T.border}` }}>
        {[
          ['candidati', 'Candidati'],
          ['consulenti', 'Collaboratori'],
          ['rilevazioni', 'Rilevazioni'],
          ['storico', 'Storico invii'],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            style={{
              padding: '8px 16px',
              border: 'none',
              background: 'none',
              fontSize: 13,
              fontWeight: 600,
              color: tab === key ? '#185FA5' : T.txt2,
              borderBottom: tab === key ? '2px solid #185FA5' : '2px solid transparent',
              cursor: 'pointer',
              marginBottom: -1,
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'candidati' && <SchedaCandidati />}
      {tab === 'consulenti' && <SchedaConsulenti />}
      {tab === 'rilevazioni' && <SchedaRilevazioni />}
      {tab === 'storico' && <SchedaStoricoInvii />}
    </div>
  );
}
